import { useNavigate } from "react-router-dom";
import { Button as MuiButton, IconButton, Card, CardContent, Typography, CircularProgress } from "@mui/material";
import { styled } from "@mui/material/styles";

interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'tertiary' | 'icon' | 'card';
  title?: string;
  text?: string;
  type?: 'button' | 'submit' | 'reset';
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  onClick?: () => void;
  path?: string;
  disabled?: boolean;
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  loading?: boolean;
  loadingText?: string;
  customCSS?: string;
  sx?: object;
}

const StyledCard = styled(Card)(({ theme }) => ({
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  },
}));

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  title = '',
  type = 'button',
  text,
  leftIcon,
  rightIcon,
  onClick,
  path,
  disabled = false,
  color = 'primary',
  loading = false,
  loadingText = 'Loading...',
  customCSS = '',
  sx = {},
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (path) {
      navigate(path);
    }
  };

  if (variant === 'primary') {
    return (
      <MuiButton
        type={type}
        variant="contained"
        color={color}
        onClick={handleClick}
        disabled={disabled || loading}
        startIcon={!loading && leftIcon}
        endIcon={!loading && rightIcon}
        sx={{ 
          textTransform: 'none',
          ...sx 
        }}
        className={customCSS}
      >
        {loading ? (
          <>
            <CircularProgress size={16} sx={{ mr: 1 }} />
            {loadingText}
          </>
        ) : (
          text
        )}
      </MuiButton>
    );
  }

  if (variant === 'secondary') {
    return (
      <MuiButton
        type={type}
        variant="outlined"
        color={color}
        onClick={handleClick}
        disabled={disabled || loading}
        startIcon={!loading && leftIcon}
        endIcon={!loading && rightIcon}
        sx={{ 
          textTransform: 'none',
          ...sx 
        }}
        className={customCSS}
      >
        {loading ? (
          <>
            <CircularProgress size={16} sx={{ mr: 1 }} />
            {loadingText}
          </>
        ) : (
          text
        )}
      </MuiButton>
    );
  }

  if (variant === 'tertiary') {
    return (
      <MuiButton
        type={type}
        variant="text"
        color={color}
        onClick={handleClick}
        disabled={disabled || loading}
        startIcon={!loading && leftIcon}
        endIcon={!loading && rightIcon}
        sx={{ 
          textTransform: 'none',
          ...sx 
        }}
        className={customCSS}
      >
        {loading ? (
          <>
            <CircularProgress size={16} sx={{ mr: 1 }} />
            {loadingText}
          </>
        ) : (
          text
        )}
      </MuiButton>
    );
  }

  if (variant === 'icon') {
    return (
      <IconButton
        type={type}
        title={title}
        onClick={handleClick}
        disabled={disabled || loading}
        color={color}
        sx={sx}
        className={customCSS}
      >
        {loading ? (
          <CircularProgress size={20} />
        ) : (
          leftIcon || rightIcon
        )}
      </IconButton>
    );
  }

  if (variant === 'card') {
    return (
      <StyledCard
        onClick={handleClick}
        sx={{ 
          opacity: disabled ? 0.6 : 1,
          pointerEvents: disabled ? 'none' : 'auto',
          ...sx 
        }}
        className={customCSS}
      >
        <CardContent sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          textAlign: 'center',
          p: 2,
          '&:last-child': { pb: 2 }
        }}>
          {leftIcon}
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
            {text}
          </Typography>
        </CardContent>
      </StyledCard>
    );
  }

  return null;
};

export default Button;